import { Link } from 'react-router-dom';
import { Avatar, Spin, Tag, Tooltip, Button, Typography } from 'antd';
import { LockOutlined, UnlockOutlined, FileTextOutlined, UserOutlined, PlusOutlined } from '@ant-design/icons';

import CreateProjectModal from '../components/CreateProjectModal';
import ProjectUsers from '../components/ProjectUsers';
import InvitationList from '../components/InvitationList';
import AddUserToProjectModal from '../components/AddUserToProjectModal';

import useBoolean from '../hooks/useBoolean';
import { usePrompt } from '../hooks/usePrompt';
import { useState, useMemo } from 'react';
import { useAddInvitationMutation, useGetProjectsQuery, useCreateProjectMutation, useRemoveUserFromProjectMutation, useDeleteInvitationMutation } from '../store/projects';
import { useSelector } from 'react-redux';

import type { RootState } from '../store';
import { USER_ROLES, PROJECT_TYPES } from '../constants';

import fuzzy from 'fuzzy';

const { Title, Text } = Typography;

function ProjectsPage() {
    const { promptModal } = usePrompt();
    const settings = useSelector((state: RootState) => state.settings);
    const [addInvitation] = useAddInvitationMutation();
    const [createProject] = useCreateProjectMutation();
    const [removeUserFromProject] = useRemoveUserFromProjectMutation();
    const [deleteInvitation] = useDeleteInvitationMutation();
    const { data, isLoading, error } = useGetProjectsQuery();
    const isOpenCreateModal = useBoolean();
    const isOpenAddUserModal = useBoolean();
    const isOpenUsersModal = useBoolean();
    const [projectId, setProjectId] = useState<number | null>(null);

    const editableProject = useMemo(() => {
        return data?.message.find(project => project.id === projectId);
    }, [data, projectId]);

    const showUsersModal = (id: number) => {
        setProjectId(id);
        isOpenUsersModal.set(true);
    };

    const handleCreateProject = async (name: string, type: number) => {
        await createProject({ name, type });
    };

    const handleAddInvitation = async (id: number, username: string, role: number) => {
        await addInvitation({
            username: username.slice(1),
            projectId: id,
            role,
        });
    };

    const handleRemoveUserFromProject = async (id: number, userId: number) => {
        await removeUserFromProject({ projectId: id, userId });
    };

    const handleDeleteInvitation = async (id: number, username: string) => {
        await deleteInvitation({ projectId: id, username });
    };

    const filtredProjects = useMemo(() => {
        if (!data) {
            return [];
        }

        return fuzzy.filter(settings.search, data.message, {
            extract: (project) => `${project.ownerName} / ${project.name}`,
            pre: '<mark>',
            post: '</mark>',
        }).map((resultItem) => ({
            contentMatch: resultItem.string,
            original: resultItem.original,
        }));
    }, [settings.search, data]);

    if (isLoading) {
        return (
            <div className="app-page" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="app-page">
                <div className="app-empty">Не удалось загрузить проекты. Обновите страницу или попробуйте позже.</div>
            </div>
        );
    }

    return (
        <div className="app-page">
            <div className="app-page__header">
                <Title level={3} className="app-page__title">
                    Проекты
                </Title>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => isOpenCreateModal.set(true)}>
                    Создать проект
                </Button>
            </div>

            {filtredProjects.length === 0 ? (
                <div className="app-empty">
                    {settings.search
                        ? 'Ничего не найдено по текущему запросу'
                        : 'Пока нет проектов — создайте первый'}
                </div>
            ) : (
                <div className="app-row-list">
                    {filtredProjects.map(({ original: project, contentMatch }) => (
                        <div key={project.id} className="app-row">
                            <div className="app-row__main">
                                <Avatar style={{ background: 'var(--app-primary)', flexShrink: 0 }}>
                                    {project.ownerName[0]}
                                </Avatar>
                                <div style={{ minWidth: 0 }}>
                                    <div className="app-row__title">
                                        <Link to={`/projects/${project.url}`}>
                                            <span dangerouslySetInnerHTML={{ __html: contentMatch }} />
                                        </Link>
                                    </div>
                                    <div className="app-row__meta" style={{ marginTop: 4 }}>
                                        <Tooltip title={project.type === PROJECT_TYPES.public ? 'Публичный проект' : 'Приватный проект'}>
                                            <Text type="secondary" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                                {project.type === PROJECT_TYPES.public ? <UnlockOutlined /> : <LockOutlined />}
                                            </Text>
                                        </Tooltip>
                                        <Tag color={project.role === USER_ROLES.owner ? 'blue' : 'green'} style={{ margin: 0 }}>
                                            {USER_ROLES[project.role]}
                                        </Tag>
                                    </div>
                                </div>
                            </div>

                            <div className="app-row__aside">
                                <Tooltip title="Эпиков">
                                    <Link to={`/projects/${project.url}`} className="app-muted" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                        <FileTextOutlined />
                                        {project.tasksCount}
                                    </Link>
                                </Tooltip>
                                <Tooltip title="Участники">
                                    <button
                                        type="button"
                                        className="app-muted"
                                        style={{ background: 'none', border: 0, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, padding: 0 }}
                                        onClick={() => showUsersModal(project.id)}
                                    >
                                        <UserOutlined />
                                        {project.usersCount}
                                    </button>
                                </Tooltip>
                                <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                                    {new Date(project.createDate).toLocaleDateString('ru-RU')}
                                </Text>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <div className="app-page__section">
                <InvitationList />
            </div>

            <CreateProjectModal
                open={isOpenCreateModal.value}
                onClose={() => isOpenCreateModal.set(false)}
                onCreate={handleCreateProject}
            />

            {projectId && <>
                <ProjectUsers
                    open={isOpenUsersModal.value}
                    onClose={() => isOpenUsersModal.set(false)}
                    onAddInvitation={() => isOpenAddUserModal.set(true)}
                    onRemoveUserFromProject={handleRemoveUserFromProject}
                    onDeleteInvitation={handleDeleteInvitation}
                    isOwner={editableProject?.role === USER_ROLES.owner}
                    projectId={projectId}
                />

                <AddUserToProjectModal
                    open={isOpenAddUserModal.value}
                    onClose={() => isOpenAddUserModal.set(false)}
                    onAdd={(name: string, role: number) => handleAddInvitation(projectId, name, role)}
                />
            </>}

            {promptModal}
        </div>
    );
}

export default ProjectsPage;
